import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Users, Check, Search, Loader2, UserCheck } from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { useModalLayer } from '../../lib/modalStack';
import { notifyToast } from '../../lib/toastStore';

/**
 * TEAMS & ROLES: this modal assigns WORKSPACE TEAM members (internal staff
 * caseworkers) to applicants. It is neither workspace-team membership
 * management (Workspace Settings) nor an organization team surface — see
 * the canonical note above isOrgConsoleProfile in serverWorkspaces.ts.
 */
interface WorkspaceTeamMember {
  uid: string;
  email: string;
  name: string;
  role: string;
  profilePhotoUrl?: string;
}

interface WorkspaceTeamAssignModalProps {
  open: boolean;
  applicationIds: string[];
  onClose: () => void;
  onAssigned?: (assignedMembers: WorkspaceTeamMember[]) => void;
}

export default function WorkspaceTeamAssignModal({ open, applicationIds, onClose, onAssigned }: WorkspaceTeamAssignModalProps) {
  const z = useModalLayer(open, onClose);
  const [members, setMembers] = useState<WorkspaceTeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchMembers = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const data = await safeFetch('/api/admin/team-members');
      setMembers((data.members || []).filter((m: WorkspaceTeamMember) => m.uid && m.uid !== '__current__'));
    } catch (e: any) {
      notifyToast('error', e.message || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    setSelected(new Set());
    setSearch('');
    if (open) fetchMembers();
  }, [open, fetchMembers]);

  const toggle = (uid: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const handleAssign = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      const data = await safeFetch('/api/admin/applications/assign-team-members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applicationIds,
          memberIds: Array.from(selected),
        }),
      });
      const assignedMembers = members.filter(m => selected.has(m.uid));
      notifyToast('success', `Assigned ${selected.size} team member(s) to ${data.updated || applicationIds.length} applicant(s).`);
      onAssigned?.(assignedMembers);
      onClose();
    } catch (e: any) {
      notifyToast('error', e.message || 'Failed to assign team members');
    } finally {
      setSaving(false);
    }
  };

  const filtered = members.filter(m => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
  });

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: z }} onClick={() => { if (!saving) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scaleIn" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <UserCheck className="h-4 w-4 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900">Assign to Team Member(s)</h3>
                  <p className="text-[11px] text-slate-500 font-semibold">{applicationIds.length} applicant(s) · select one or more members</p>
                </div>
              </div>
              <button aria-label="Close" title="Close" onClick={() => { if (!saving) onClose(); }} className="p-1.5 rounded-lg hover:bg-slate-100 transition cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>

            <div className="p-4">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search team members..."
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-xs font-semibold text-slate-700 outline-none focus:bg-white"
                />
              </div>

              {loading ? (
                <div className="py-12 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
              ) : filtered.length === 0 ? (
                <p className="text-xs text-slate-500 italic py-10 text-center">
                  {members.length === 0 ? 'No team members found. Add team members in Workspace Settings → Team.' : 'No members match your search.'}
                </p>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {filtered.map(m => (
                    <button
                      key={m.uid}
                      onClick={() => toggle(m.uid)}
                      className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all cursor-pointer ${selected.has(m.uid) ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                    >
                      <span className={`h-4 w-4 rounded-md border flex items-center justify-center shrink-0 ${selected.has(m.uid) ? 'bg-brand-primary border-brand-primary' : 'border-slate-300 bg-white'}`}>
                        {selected.has(m.uid) && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-800 truncate">{m.name}</p>
                        <p className="text-[11px] text-slate-500 truncate">{m.email}</p>
                      </div>
                      <span className="shrink-0 text-[11px] font-black uppercase text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md truncate max-w-[90px]">{m.role}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between gap-2">
              <span className="text-[11px] font-bold text-slate-500 flex items-center gap-1">
                <Users className="h-3.5 w-3.5 text-slate-400" /> {selected.size} selected
              </span>
              <div className="flex gap-2">
                <button onClick={() => { if (!saving) onClose(); }} className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer">
                  Cancel
                </button>
                <button
                  onClick={handleAssign}
                  disabled={saving || selected.size === 0}
                  className="px-4 py-2 bg-brand-primary text-white text-xs font-bold rounded-xl hover:bg-brand-primary-dark disabled:opacity-50 transition cursor-pointer flex items-center gap-1.5"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} {saving ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        </div>,
    document.body
  );
}
